import React, { useState } from "react";
import { Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";

/**
 * Crawler-friendly share button.
 *
 * Builds a URL like `${API_BASE}/share/team/{slug}` (or /share/news/{id}) that
 * the backend serves with proper OG/Twitter meta tags + auto-redirect to the
 * SPA. JS-less crawlers (WhatsApp, Slack, Discord, LinkedIn) see the correct
 * preview; real users get redirected to the canonical SPA URL.
 *
 * Side effect: fires POST /api/shares (fire-and-forget) to log the click for
 * the "Most-Shared" leaderboard + admin Share Velocity panel.
 */
export default function ShareButton({ kind, slug, title, className = "", label = "Share" }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = `${API_BASE}/share/${kind}/${slug}`;
  const shareTitle = title || "PSHL8 — Pro Skate Hockey League";

  const logShare = () => {
    // Fire-and-forget. Don't block the UX if logging fails.
    api.post("/shares", {
      kind,
      target_id: slug,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    }).catch(() => { /* swallow */ });
  };

  const onClick = async (e) => {
    e.preventDefault();
    logShare();
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return;
      } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[ShareButton] native share fallback", err); }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied — preview will render on WhatsApp, X, LinkedIn, Slack & more.");
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[ShareButton] clipboard write failed", err);
      window.open(shareUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`share-button-${kind}-${slug}`}
      className={`inline-flex items-center gap-2 border border-white/20 hover:border-pshl-electric hover:text-pshl-electric text-white font-display uppercase tracking-[0.18em] text-xs px-4 py-2.5 transition-colors ${className}`}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
      {copied ? "Copied" : label}
    </button>
  );
}
